import { useCallback, useState } from "react";
import axios from "axios";
import type { TimeReportBulkErrorKey } from "hooks/timeReportErrors";
import { resolveBulkErrorKeyFromApiError } from "hooks/timeReportErrors";
import { Api } from "services/api/api.service";
import { buildApprovePayload } from "../models/personView.actions";
import type { BulkAction } from "../models/personView.actions";
import type { PersonDetailSelection } from "../models/personView.types";

export type FeedbackState =
  | { type: "success" }
  | {
      type: "partial";
      succeededCount: number;
      failedCount: number;
      failedItems: FailedBulkActionItem[];
    }
  | { type: "error"; failedItems: FailedBulkActionItem[] }
  | null;

export interface FailedBulkActionItem {
  timeReportId: number;
  workerName: string;
  dateLabel: string;
  reasonKey: TimeReportBulkErrorKey;
}

type CommentableBulkAction = Exclude<BulkAction, "absence">;

interface UsePersonBulkActionsParams {
  companyId: number | undefined;
  selectedDetails: PersonDetailSelection[];
  openedDetail: PersonDetailSelection | null;
  selectedDetailIdSet: Set<number>;
  clearSelection: () => void;
  retainSelectionByIds: (ids: Set<number>) => void;
  setOpenedDetail: (selection: PersonDetailSelection | null) => void;
  reload: () => void;
}

interface UsePersonBulkActionsState {
  canRunActions: boolean;
  isBulkActionLoading: boolean;
  isAbsenceModalOpen: boolean;
  absenceTargetIds: number[];
  bulkCommentAction: CommentableBulkAction | null;
  feedbackState: FeedbackState;
  runBulkAction: (action: BulkAction) => Promise<void>;
  runCommentableBulkAction: (
    action: CommentableBulkAction,
    comment: string,
  ) => Promise<boolean>;
  closeAbsenceModal: () => void;
  handleAbsenceReported: (payload: {
    succeededCount: number;
    failedCount: number;
    failedIds: number[];
    failedItems?: Array<{
      timeReportId: number;
      reasonKey: TimeReportBulkErrorKey;
    }>;
  }) => void;
  closeBulkCommentModal: () => void;
  clearFeedback: () => void;
  showActionError: () => void;
}

const runSelectionAction = async (
  action: CommentableBulkAction,
  companyId: number,
  selection: PersonDetailSelection,
  comment: string,
): Promise<void> => {
  const timeReportsApi = Api().company.timeReports;
  if (action === "retract") {
    await timeReportsApi.withdrawTimeReport(
      companyId,
      selection.item.id,
      comment,
    );
    return;
  }
  if (action === "reset") {
    await timeReportsApi.reopenTimeReport(
      companyId,
      selection.item.id,
      comment,
    );
    return;
  }
  if (action === "reject") {
    await timeReportsApi.rejectTimeReport(
      companyId,
      selection.item.id,
      comment,
    );
    return;
  }

  await timeReportsApi.approveTimeReport(companyId, selection.item.id, {
    ...buildApprovePayload(selection.item),
    comment,
  });
};

export const usePersonBulkActions = (
  params: UsePersonBulkActionsParams,
): UsePersonBulkActionsState => {
  const {
    companyId,
    selectedDetails,
    openedDetail,
    selectedDetailIdSet,
    clearSelection,
    retainSelectionByIds,
    setOpenedDetail,
    reload,
  } = params;

  const [isBulkActionLoading, setIsBulkActionLoading] = useState(false);
  const [isAbsenceModalOpen, setIsAbsenceModalOpen] = useState(false);
  const [absenceTargetIds, setAbsenceTargetIds] = useState<number[]>([]);
  const [bulkCommentAction, setBulkCommentAction] =
    useState<CommentableBulkAction | null>(null);
  const [feedbackState, setFeedbackState] = useState<FeedbackState>(null);

  const canRunActions = Boolean(companyId && selectedDetails.length > 0);

  const showActionSuccess = useCallback((): void => {
    setFeedbackState({ type: "success" });
  }, []);

  const showActionError = useCallback((): void => {
    setFeedbackState({ type: "error", failedItems: [] });
  }, []);

  const runCommentableBulkAction = useCallback(
    async (
      action: CommentableBulkAction,
      comment: string,
    ): Promise<boolean> => {
      if (!canRunActions || isBulkActionLoading || !companyId) {
        return false;
      }

      setIsBulkActionLoading(true);
      try {
        let succeededCount = 0;
        let failedCount = 0;
        const failedIds = new Set<number>();
        const failedItems: FailedBulkActionItem[] = [];

        for (const selection of selectedDetails) {
          try {
            await runSelectionAction(action, companyId, selection, comment);
            succeededCount += 1;
          } catch (error: unknown) {
            failedCount += 1;
            failedIds.add(selection.item.id);
            failedItems.push({
              timeReportId: selection.item.id,
              workerName: selection.workerName,
              dateLabel: selection.detail.dateLabel,
              reasonKey: axios.isAxiosError(error)
                ? resolveBulkErrorKeyFromApiError(error)
                : "CompanyTimeReport.UnexpectedError",
            });
          }
        }

        if (failedCount === 0) {
          showActionSuccess();
          clearSelection();
        } else if (succeededCount === 0) {
          setFeedbackState({ type: "error", failedItems });
        } else {
          setFeedbackState({
            type: "partial",
            succeededCount,
            failedCount,
            failedItems,
          });
          retainSelectionByIds(failedIds);
        }

        if (
          openedDetail &&
          selectedDetailIdSet.has(openedDetail.item.id) &&
          !failedIds.has(openedDetail.item.id)
        ) {
          setOpenedDetail(null);
        }
      } finally {
        reload();
        setIsBulkActionLoading(false);
      }

      return true;
    },
    [
      canRunActions,
      companyId,
      clearSelection,
      isBulkActionLoading,
      reload,
      retainSelectionByIds,
      openedDetail,
      selectedDetailIdSet,
      selectedDetails,
      setOpenedDetail,
      showActionSuccess,
    ],
  );

  const runBulkAction = useCallback(
    async (action: BulkAction): Promise<void> => {
      if (!canRunActions || isBulkActionLoading || !companyId) return;

      if (action === "absence") {
        setAbsenceTargetIds(
          selectedDetails.map((selection) => selection.item.id),
        );
        setIsAbsenceModalOpen(true);
        return;
      }

      setBulkCommentAction(action);
    },
    [canRunActions, companyId, isBulkActionLoading, selectedDetails],
  );

  const closeAbsenceModal = useCallback((): void => {
    setIsAbsenceModalOpen(false);
    setAbsenceTargetIds([]);
  }, []);

  const handleAbsenceReported = useCallback(
    (payload: {
      succeededCount: number;
      failedCount: number;
      failedIds: number[];
      failedItems?: Array<{
        timeReportId: number;
        reasonKey: TimeReportBulkErrorKey;
      }>;
    }): void => {
      const { succeededCount, failedCount, failedIds } = payload;
      closeAbsenceModal();
      if (failedCount === 0) {
        clearSelection();
        showActionSuccess();
      } else {
        const failedSelectionById = new Map<number, PersonDetailSelection>(
          selectedDetails.map((selection) => [selection.item.id, selection]),
        );
        const mappedFailedItems: FailedBulkActionItem[] = failedIds.map(
          (timeReportId) => {
            const selection = failedSelectionById.get(timeReportId);
            const reportedFailure = payload.failedItems?.find(
              (item) => item.timeReportId === timeReportId,
            );
            return {
              timeReportId,
              workerName: selection?.workerName ?? "",
              dateLabel: selection?.detail.dateLabel ?? "",
              reasonKey:
                reportedFailure?.reasonKey ??
                "CompanyTimeReport.UnexpectedError",
            };
          },
        );
        setFeedbackState({
          type: "partial",
          succeededCount,
          failedCount,
          failedItems: mappedFailedItems,
        });
        retainSelectionByIds(new Set(failedIds));
      }
      if (
        openedDetail &&
        selectedDetailIdSet.has(openedDetail.item.id) &&
        !failedIds.includes(openedDetail.item.id)
      ) {
        setOpenedDetail(null);
      }
      reload();
    },
    [
      clearSelection,
      closeAbsenceModal,
      reload,
      retainSelectionByIds,
      selectedDetails,
      openedDetail,
      selectedDetailIdSet,
      setOpenedDetail,
      showActionSuccess,
    ],
  );

  const closeBulkCommentModal = useCallback((): void => {
    setBulkCommentAction(null);
  }, []);

  const clearFeedback = useCallback((): void => {
    setFeedbackState(null);
  }, []);

  return {
    canRunActions,
    isBulkActionLoading,
    isAbsenceModalOpen,
    absenceTargetIds,
    bulkCommentAction,
    feedbackState,
    runBulkAction,
    runCommentableBulkAction,
    closeAbsenceModal,
    handleAbsenceReported,
    closeBulkCommentModal,
    clearFeedback,
    showActionError,
  };
};
